// src/pages/ProductCatalog.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { uploadProductsExcel } from '../services/products';
import AddProductModal from '../components/AddProductModal';
import { Product } from '../pages/ProductContent/Product';

const ProductCatalog: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [productToEdit, setProductToEdit] = useState<Product | null>(null);
  const [uploading, setUploading] = useState(false);
  
  // Search states
  const [searchMethod, setSearchMethod] = useState<'direct' | 'filter'>('direct');
  const [brands, setBrands] = useState<string[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data);
      setFilteredProducts(res.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/inventory/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(res.data.brands || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchBrands();
  }, []);

  // Filter products based on search method and query
  useEffect(() => {
    let filtered = [...products];

    if (searchMethod === 'direct') {
      // Direct search: search across product code and name
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(p => 
          (p.no?.toLowerCase().includes(query)) ||
          (p.name?.toLowerCase().includes(query))
        );
      }
    } else {
      // Filter method: first filter by brand, then by query
      if (selectedBrand) {
        filtered = filtered.filter(p => p.category === selectedBrand);
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(p => 
          (p.no?.toLowerCase().includes(query)) ||
          (p.name?.toLowerCase().includes(query))
        );
      }
    }

    setFilteredProducts(filtered);
  }, [searchMethod, selectedBrand, searchQuery, products]);

  const onExcelChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget; // cache before async to avoid React nulling
    const file = inputEl.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadProductsExcel(file);
      await fetchProducts();
      alert('Products uploaded');
    } catch (err) {
      console.error(err);
      alert('Excel upload failed');
    } finally {
      setUploading(false);
      if (inputEl) inputEl.value = '';
    }
  };

  const handleEdit = (product: Product) => {
    setProductToEdit(product);
    setShowModal(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Product Catalog</h2>
        <button
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={() => {
            setProductToEdit(null); // for add mode
            setShowModal(true);
          }}
        >
          + Add Product
        </button>
      </div>

      {/* Search Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              value="direct"
              checked={searchMethod === 'direct'}
              onChange={(e) => {
                setSearchMethod('direct');
                setSelectedBrand('');
                setSearchQuery('');
              }}
            />
            <span className="text-sm font-medium">Direct Search</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              value="filter"
              checked={searchMethod === 'filter'}
              onChange={(e) => {
                setSearchMethod('filter');
                setSearchQuery('');
              }}
            />
            <span className="text-sm font-medium">Filter by Brand</span>
          </label>
        </div>

        <div className="flex items-center gap-3">
          {searchMethod === 'filter' && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Brand:</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="px-3 py-2 border rounded-md"
              >
                <option value="">Select Brand</option>
                {brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="flex-1 flex items-center gap-2">
            <label className="text-sm font-medium">
              {searchMethod === 'direct' ? 'Search:' : 'Product:'}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                searchMethod === 'direct'
                  ? 'Search by product code or name...'
                  : selectedBrand
                  ? 'Search by product code or name in selected brand...'
                  : 'Please select a brand first'
              }
              disabled={searchMethod === 'filter' && !selectedBrand}
              className="flex-1 px-3 py-2 border rounded-md disabled:bg-gray-100"
            />
            {(searchQuery || selectedBrand) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedBrand('');
                }}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-2 text-sm text-gray-600">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm">Upload Excel:</label>
        <input type="file" accept=".xlsx,.xls" onChange={onExcelChange} disabled={uploading} />
        {uploading && <span className="text-sm text-gray-500">Uploading…</span>}
      </div>

      <table className="w-full border table-auto text-sm">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-2">Product Code</th>
            <th className="p-2">Name</th>
            <th className="p-2">Unit</th>
            <th className="p-2">MRP</th>
            {/* <th className="p-2">Category</th> */}
            <th className="p-2">HSN</th>
            <th className="p-2">GST%</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.length === 0 ? (
            <tr>
              <td colSpan={7} className="p-4 text-center text-gray-500">
                No products found matching your search criteria
              </td>
            </tr>
          ) : (
            filteredProducts.map((prod) => (
              <tr key={prod._id} className="text-center border-t">
                <td className="p-2">{prod.no}</td>
                <td className="p-2">{prod.name}</td>
                <td className="p-2">{prod.unit}</td>
                <td className="p-2">{prod.mrp}</td>
                {/* <td className="p-2">{prod.category}</td> */}
                <td className="p-2">{prod.hsncode}</td>
                <td className="p-2">{prod.gstpercentage}</td>
                <td className="p-2">
                  <button
                    className="text-blue-500 underline"
                    onClick={() => handleEdit(prod)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showModal && (
        <AddProductModal
          isEdit={!!productToEdit}
          initialData={productToEdit || undefined}
          onClose={() => {
            setShowModal(false);
            fetchProducts();
          }}
        />
      )}
    </div>
  );
};

export default ProductCatalog;

// // src/pages/ProductCatalog.tsx
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import { Product } from '../pages/ProductContent/Product';
// import AddProductModal from '../components/AddProductModal';

// const ProductCatalog: React.FC = () => {
//   const [products, setProducts] = useState<Product[]>([]);
//   const [showModal, setShowModal] = useState(false);
// const [productToEdit, setProductToEdit] = useState<Product | null>(null); // 🔧 Add this


//   const fetchProducts = async () => {
//     const token = localStorage.getItem('token');
//     const res = await axios.get('https://api.mangrishprince.com/api/v1/products', {
//       headers: { Authorization: `Bearer ${token}` },
//     });
//     setProducts(res.data);
//   };
//   const handleAdd = () => {
//   setProductToEdit(null); // 🆕 Clear previous product
//   setShowModal(true);
// };
// const handleEdit = (product: Product) => {
//   setProductToEdit(product); // 🆕 Set product to edit
//   setShowModal(true);
// };

//   const deleteProduct = async (id: string) => {
//     const token = localStorage.getItem('token');
//     if (!window.confirm('Are you sure you want to delete this product?')) return;

//     await axios.delete(`https://api.mangrishprince.com/api/v1/products/${id}`, {
//       headers: { Authorization: `Bearer ${token}` },
//     });
//     fetchProducts();
//   };

//   useEffect(() => {
//     fetchProducts();
//   }, []);

//   return (
//     <div className="p-6">
//       <div className="flex justify-between items-center mb-4">
//         <h1 className="text-2xl font-bold">Product Catalog</h1>
//         <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded">
//           + Add Product
//         </button>
//       </div>

//       {products.length === 0 ? (
//         <p>No products found.</p>
//       ) : (
//         <table className="w-full table-auto border">
//           <thead className="bg-gray-100">
//             <tr>
//               <th className="px-2 py-1">Name</th>
//               <th>Size</th>
//               <th>Unit</th>
//               <th>MRP</th>
//               <th>Category</th>
//               <th>HSN</th>
//               <th>GST%</th>
//               <th>Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {products.map((prod) => (
//               <tr key={prod._id} className="text-center border-t">
//                 <td>{prod.name}</td>
//                 <td>{prod.size}</td>
//                 <td>{prod.unit}</td>
//                 <td>{prod.mrp}</td>
//                 <td>{prod.category}</td>
//                 <td>{prod.hsncode}</td>
//                 <td>{prod.gstpercentage}</td>
//                 <td className="space-x-2">
//                   <button
//                     className="text-blue-600"
//                     onClick={() => alert('Implement Edit Modal Here')}
//                   >
//                     ✏️ Edit
//                   </button>
//                   <button className="text-red-600" onClick={() => deleteProduct(prod._id!)}>
//                     🗑 Delete
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       )}

//       {showModal && <AddProductModal isEdit={true} initialData={productToEdit} onClose={() => { setShowModal(false); fetchProducts(); }} />}
//     </div>
//   );
// };

// export default ProductCatalog;
